import type { ReactNode } from "react";
import {
  View,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import { type LucideIcon } from "lucide-react-native";
import { semanticColors, typography } from "../theme/tokens";
import { MoonText } from "./MoonText";
import { GradientIcon, type GradientVariant } from "./GradientIcon";

/**
 * "Nothing yet" placeholder for empty lists / sections. Audit task 2.1.8.
 *
 * Canonicalises the two recurring shapes in moon:
 *   - bare: icon + one-line message (CycleDetailScreen `EmptySection`).
 *     Plain Lucide icon, flat color, subtle and faded.
 *   - rich: brand-gradient `GradientIcon` inside a tinted orb + title +
 *     subtitle + optional CTA action (CryoVaultScreen specimens empty
 *     state). The gradient stroke matches the moon screen-title pattern
 *     so featured empty states read as on-brand placeholders rather than
 *     debug screens.
 *
 * The `iconOrb` prop swaps between the two. For the orb variant the icon
 * stroke gets the warm brand gradient via `GradientIcon` (default
 * "ember"); override with `gradient` to pick another brand variant.
 *
 * State coverage (Phase 1.0 matrix):
 *   - idle: default
 *   - loading: consumers wrap in MoonSkeleton or use the loading prop on
 *     their parent list — empty state is a terminal state, not a phase.
 *   - error: surface a separate error state instead of overloading this.
 */
type Props = {
  /** Leading icon. */
  icon?: LucideIcon;
  /** Optional illustration rendered above the title instead of the icon. */
  illustration?: ReactNode;
  /** Title line — required, the main "what's empty" message. */
  title: string;
  /** Optional explanation below the title. */
  subtitle?: string;
  /** Optional style override for the supporting explanation. */
  subtitleStyle?: StyleProp<TextStyle>;
  /** Optional CTA element (button) rendered below the subtitle. */
  action?: React.ReactNode;
  /** Wrap the icon in a circular tinted background and render the icon
   *  with `GradientIcon`. Defaults true (premium orb look). Pass
   *  `iconOrb={false}` for inline list-empty placeholders where a faded
   *  one-liner reads better than a featured orb. */
  iconOrb?: boolean;
  /** Brand gradient variant for the orb-icon stroke. Defaults to "ember"
   *  (deep brown). Only applies when `iconOrb` is true. */
  gradient?: GradientVariant;
  /** Vertical footprint. "regular" is the featured 28px-padded block;
   *  "compact" halves the padding and gap for inline section placeholders
   *  (e.g. the stimulation-monitoring empty state) so the card doesn't
   *  dominate the surrounding screen. Prefer this over per-call-site
   *  padding overrides in `style`. */
  density?: "regular" | "compact";
  /** Hue used for the orb's tinted background and the bare-icon stroke.
   *  Defaults to textFaint. The orb icon itself uses `gradient`, not
   *  this color, so the orb bg can be one hue while the icon stroke
   *  stays on-brand. */
  accentColor?: string;
  testID?: string;
  style?: ViewStyle;
};

const ICON_SIZE = 24;
const ORB_SIZE = 56;

export function MoonEmptyState({
  icon: Icon,
  illustration,
  title,
  subtitle,
  subtitleStyle,
  action,
  iconOrb = true,
  gradient = "ember",
  density = "regular",
  accentColor = semanticColors.text.faint,
  testID,
  style,
}: Props) {
  const isCompact = density === "compact";
  return (
    <View
      testID={testID}
      style={[
        {
          alignItems: "center",
          paddingVertical: isCompact ? 14 : 28,
          paddingHorizontal: 16,
          gap: isCompact ? 4 : 8,
        },
        style,
      ]}
    >
      {illustration ? (
        <View style={{ marginBottom: 4 }}>{illustration}</View>
      ) : Icon ? (
        iconOrb ? (
          <View
            style={{
              width: ORB_SIZE,
              height: ORB_SIZE,
              borderRadius: ORB_SIZE / 2,
              backgroundColor: `${accentColor}1A`,
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 6,
            }}
          >
            <GradientIcon
              icon={Icon}
              size={ICON_SIZE}
              strokeWidth={1.5}
              variant={gradient}
            />
          </View>
        ) : (
          <Icon size={20} color={accentColor} strokeWidth={1.5} />
        )
      ) : null}
      <MoonText
        style={{
          fontFamily: iconOrb
            ? typography.families.sansMedium
            : typography.families.sans,
          fontSize: iconOrb ? 16 : 13,
          fontWeight: iconOrb ? "600" : "400",
          color: iconOrb
            ? semanticColors.text.primary
            : semanticColors.text.tertiary,
          textAlign: "center",
          lineHeight: iconOrb ? 22 : 19,
        }}
      >
        {title}
      </MoonText>
      {subtitle ? (
        <MoonText
          variant="body-sm"
          color="textLight"
          style={[
            {
              textAlign: "center",
              lineHeight: 18,
              marginTop: iconOrb ? 4 : 0,
            },
            subtitleStyle,
          ]}
        >
          {subtitle}
        </MoonText>
      ) : null}
      {action ? (
        <View style={{ marginTop: isCompact ? 8 : 12 }}>{action}</View>
      ) : null}
    </View>
  );
}
